import clsx from 'clsx'
import { computed, defineComponent } from 'vue'

import { RouterLink, useRouter } from 'vue-router'

import MenuButton from '@/components/common/button/MenuButton'
import VIcon from '@/components/common/VIcon'
import VMenu, { type VMenuActivatorProps } from '@/components/common/VMenu'
import useAuth from '@/composables/auth/useAuth'
import useBreakpoints from '@/composables/common/useBreakpoints'
import useLocalization from '@/composables/localization/useLocalization'
import useNavbar from '@/composables/navbar/useNavbar'

import ProfileMenuModal from './ProfileMenuModal'

export default defineComponent(() => {
  const auth = useAuth()
  const router = useRouter()
  const { localeTexts } = useLocalization()
  const { isMobile } = useBreakpoints()
  const { profileMenuModal, handleLogout, handleShowProfileMenuModal } = useNavbar()

  // #region Getters

  const userImageProfile = computed((): JSX.Element => {
    if (auth.userProfile?.image_url !== null) {
      return (
        <img
          t-data='navbar-profile-user-image'
          class='size-5 rounded-full'
          src={auth.userProfile?.image_url}
        />
      )
    }

    return (
      <div class='relative rounded-full bg-neutral-200'>
        <VIcon
          t-data='navbar-profile-user-image-default'
          class='text-[20px] text-neutral-300'
          icon='material-symbols:account-circle-outline'
        />
      </div>
    )
  })

  // #endregion

  return () => (
    <div class='sticky top-0 z-999 flex h-navbar w-screen items-center bg-primary-100 px-8 py-2.5'>
      <div class='flex-1'>
        <RouterLink to='/'>
          <div class='text-heading-6 text-primary'>ARM</div>
        </RouterLink>
      </div>
      {auth.loggedIn &&
        <div>
          <VMenu
            t-data='navbar-profile-menu'
            menuContentClass={clsx('w-60')}
            disabled={isMobile.value}
          >
            {{
              activator: (slotProps: VMenuActivatorProps) => (
                <button
                  t-data='navbar-profile-menu-btn'
                  ref={slotProps.ref}
                  class={clsx(
                    'inline-flex h-8 items-center justify-center space-x-2 rounded px-1.5 hover:bg-neutral-200',
                    { 'bg-black/20': slotProps.isFocus }
                  )}
                  onClick={() => { isMobile.value && handleShowProfileMenuModal() }}
                >
                  {userImageProfile.value}
                </button>
              ),
              default: () => (
                <>
                  <MenuButton
                    t-data='navbar-profile-menu-item-my-account-btn'
                    onClick={() => {
                      router.push({ name: 'my-account' })
                    }}
                  >
                    {localeTexts.value['navigation-menu-item-account-btn']}
                  </MenuButton>
                  <div class='my-2 h-px w-full bg-neutral-300' />
                  <MenuButton
                    t-data='navbar-profile-menu-item-logout-btn'
                    onClick={handleLogout}
                  >
                    {localeTexts.value['navigation-menu-item-logout-btn']}
                  </MenuButton>
                </>
              )
            }}
          </VMenu>
          {profileMenuModal.isRevealed.value && <ProfileMenuModal modalkey={profileMenuModal.key} />}
        </div>}
    </div>
  )
}, {
  name: 'VNavbar'
})
